import React from "react";
import { Box, Typography, Button, Stack, Divider } from "@mui/material";
import DriveFileRenameOutlineRoundedIcon from "@mui/icons-material/DriveFileRenameOutlineRounded";
import WidgetsRoundedIcon from "@mui/icons-material/WidgetsRounded";
import FactCheckRoundedIcon from "@mui/icons-material/FactCheckRounded";
import MemoryRoundedIcon from "@mui/icons-material/MemoryRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";

const loc = (a: any) => {
  if (!a) return null;
  if (a.url) return a.url;
  if (a.bucket && a.path) return `${a.bucket}/${a.path}`;
  if (a.name) return a.name;
  return null;
};

// Resources may be a single object (legacy) or an array (one or more).
const asList = (a: any): any[] => (Array.isArray(a) ? a : a ? [a] : []);

function ReviewRow({
  icon,
  label,
  value,
  placeholder,
}: {
  icon: React.ReactNode;
  label: string;
  value?: string | null;
  placeholder?: string;
}) {
  const set = !!value;
  return (
    <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ py: 1.25 }}>
      <Box sx={{ color: set ? "primary.main" : "text.disabled", mt: 0.25, display: "flex" }}>{icon}</Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="caption" sx={{ fontWeight: 800, letterSpacing: 0.4, color: "text.secondary" }}>
          {label.toUpperCase()}
        </Typography>
        <Typography
          sx={{ fontWeight: set ? 600 : 400, color: set ? "text.primary" : "text.disabled", wordBreak: "break-all", fontSize: 14 }}
        >
          {value || placeholder || "Not selected"}
        </Typography>
      </Box>
    </Stack>
  );
}

export default function ReviewSubmit({ title, framework, cases, resources, onSubmit }: any) {
  return (
    <Box>
      <Typography sx={{ fontWeight: 800, fontSize: 18, mb: 0.5 }}>Review &amp; create</Typography>
      <Typography color="text.secondary" sx={{ fontSize: 13, mb: 1 }}>
        Confirm what DeviceSure will assemble into the suite.
      </Typography>

      <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, px: 2, mb: 2 }}>
        <ReviewRow icon={<DriveFileRenameOutlineRoundedIcon fontSize="small" />} label="Suite title" value={title} />
        <Divider />
        <ReviewRow icon={<WidgetsRoundedIcon fontSize="small" />} label="Framework" value={loc(framework)} />
        <Divider />
        <ReviewRow icon={<FactCheckRoundedIcon fontSize="small" />} label="Test cases" value={loc(cases)} />
        <Divider />
        {(() => {
          const list = asList(resources);
          if (list.length === 0) {
            return (
              <ReviewRow
                icon={<MemoryRoundedIcon fontSize="small" />}
                label="Resources"
                value={null}
                placeholder="Skipped (no extra resource)"
              />
            );
          }
          return (
            <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ py: 1.25 }}>
              <Box sx={{ color: "primary.main", mt: 0.25, display: "flex" }}>
                <MemoryRoundedIcon fontSize="small" />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="caption" sx={{ fontWeight: 800, letterSpacing: 0.4, color: "text.secondary" }}>
                  RESOURCES
                </Typography>
                {list.map((r, i) => (
                  <Typography
                    key={i}
                    sx={{ fontWeight: 600, color: "text.primary", wordBreak: "break-all", fontSize: 14 }}
                  >
                    {loc(r)}
                  </Typography>
                ))}
              </Box>
            </Stack>
          );
        })()}
      </Box>

      <Button variant="contained" size="large" startIcon={<CheckRoundedIcon />} onClick={onSubmit}>
        Create suite
      </Button>
    </Box>
  );
}
